import { useLazyQuery, useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { Option, SortModel } from 'src/components/commons/subTable/types';
import { CompanyTierModel } from 'src/graphql/schema-types';
import useInfinityDataSource, { FetchFunc } from 'src/hooks/useInfinityDataSource';
import PageInfo from 'src/modules/settings/modules/subTables/types';
import capitalizeFirstWord from 'src/utils/functions/capitalizeFirstWord';
import handleError from 'src/utils/functions/handleError';
import COLUMNS from '../constants';
import {
  CREATE_TIER_COMPANY,
  DELETE_TIER_COMPANY,
  UPDATE_TIER_COMPANY,
} from '../graphql/mutations';
import GET_ALL_TIER_COMPANIES from '../graphql/queries';
import { CompanyTier, GetAllCompanyTier } from '../types';
import useFilterColumn from '../../callPattern/hooks/useFilterColumn';

const useTierTable = () => {
  const { createdFilters, setColumnFilters } = useFilterColumn();
  const [getAll, queryResponse] = useLazyQuery<GetAllCompanyTier>(GET_ALL_TIER_COMPANIES, {
    fetchPolicy: 'network-only',
  });

  const [loadDataFilter] = useLazyQuery<GetAllCompanyTier>(GET_ALL_TIER_COMPANIES, {
    fetchPolicy: 'network-only',
  });

  const [update] = useMutation(UPDATE_TIER_COMPANY);
  const [create] = useMutation(CREATE_TIER_COMPANY);
  const [remove] = useMutation(DELETE_TIER_COMPANY);

  const [pagination, setPagination] = useState<PageInfo>({ page: 1, pageSize: 1000 });
  const [columns, setColumns] = useState(COLUMNS);
  const [newRecord, setNewRecord] = useState({
    name: '',
    description: '',
  });

  const fetchData: FetchFunc = ({ start, renderLen }) =>
    getAll({
      variables: {
        criteria: {
          pagination: {
            from: start,
            size: renderLen,
          },
        },
      },
    }).then((res) => ({
      results: res.data?.GetAllCompanyTier2.results || [],
      total: res.data?.GetAllCompanyTier2.total || 0,
    }));

  const { dataSource, onListRender, setDataSource, reset } = useInfinityDataSource(fetchData);

  useEffect(() => {
    reset();
    setColumnFilters([]);
  }, []);

  const autocompleteFieldSearch = (
    searchField: string,
    search: string,
    dataType: string | number | string[],
  ) => {
    const filters = createdFilters(searchField, search, dataType);
    return loadDataFilter({
      variables: {
        criteria: {
          pagination: {
            from: 0,
            size: 25,
          },
          filter: filters,
        },
        searchFields: {
          ids: null,
          names: null,
          enabled: null,
        },
      },
    }).then((res) => ({
      results: res?.data?.GetAllCompanyTier2.results as any[],
      filter: res?.data?.GetAllCompanyTier2.results?.map((result: CompanyTier) => ({
        value: result[searchField],
        label: result[searchField],
      })) as Option[],
    }));
  };

  const resetNewRecord = () => {
    setNewRecord({
      name: '',
      description: '',
    });
  };

  const handleClearFilter = () => {
    reset();
  };

  const createNewRecord = async () => {
    if (newRecord.name === '') {
      return;
    }

    await create({
      variables: {
        createCompanyTierInput: {
          name: capitalizeFirstWord(newRecord.name),
          description: newRecord.description,
        },
      },
    }).then(() => {
      setTimeout(() => {
        reset();
      }, 500);
    });
  };

  const handleUpdateRows = (rows: any[]) => {
    rows.forEach((row) => {
      const { id, name, description } = row;
      update({
        variables: {
          updateCompanyTierInput: {
            id,
            name: capitalizeFirstWord(name),
            description,
          },
        },
      });
    });
  };

  const handleDeleteRows = async (rows: CompanyTierModel[]) => {
    const removeId = rows[0].id;

    const messages = {
      400: {
        title: 'This record cannot be deleted',
        message: 'This value is linked to the company. You can not delete it.',
      },
    };
    try {
      await remove({ variables: { id: removeId } }).then(() => {
        setTimeout(() => {
          reset();
        }, 500);
      });
    } catch (error) {
      handleError(error, messages);
      console.log(error);
    }
  };

  const postData = (args: Record<string, unknown>) => {
    setNewRecord((prev) => ({
      ...prev,
      [args?.dataIndex as string]: args?.value as string,
      autoSave: !!args?.autoSave,
    }));
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination({ page, pageSize });
  };

  const handleFilterChange = (newFilters: Record<string, string[]>) => {
    setPagination({ ...pagination, filters: newFilters, page: 1 });
  };

  const handleSortModelChange = async (sortModel: SortModel | undefined) => {
    if (!sortModel) {
      // Load original data without any sorting
      try {
        const response = await getAll({
          variables: {
            criteria: {
              pagination: {
                from: (pagination.page - 1) * pagination.pageSize,
                size: pagination.pageSize,
              },
            },
          },
        });

        // Update data source with the original data
        if (response.data && response.data.GetAllCompanyTier2) {
          setDataSource(response.data.GetAllCompanyTier2.results);
        }
      } catch (error) {
        // Handle the error scenario
        console.error('Failed to fetch original data:', error);
      }
      return; // Early return to skip sorting if sortModel is undefined
    }

    // Check if sortBy is "parent" and change it to "parent_name"
    const sortByField = sortModel.sortBy === 'parent' ? 'parent_name' : sortModel.sortBy;

    try {
      // Update pagination with the new sort model
      setPagination((prev) => ({
        ...prev,
        sort: sortModel,
      }));

      // Fetch sorted data
      const response = await getAll({
        variables: {
          criteria: {
            pagination: {
              from: (pagination.page - 1) * pagination.pageSize,
              size: pagination.pageSize,
            },
            orderBy: [
              {
                field: sortByField,
                sort: sortModel.sort.toUpperCase(),
              },
            ],
          },
        },
      });

      // Update data source with new sorted data
      if (response.data && response.data.GetAllCompanyTier2) {
        setDataSource(response.data.GetAllCompanyTier2.results);
      }
    } catch (error) {
      // Handle the error scenario
      console.error('Failed to fetch sorted data:', error);
    }
  };

  return {
    autocompleteFieldSearch,
    handlePaginationChange,
    handleFilterChange,
    handleSortModelChange,
    pagination,
    columns,
    setColumns,
    handleUpdateRows,
    handleDeleteRows,
    newRecord,
    postData,
    createNewRecord,
    resetNewRecord,
    dataSource,
    onListRender,
    queryResponse,
    handleClearFilter,
  };
};

export default useTierTable;
